<template>
    <vx-modal :visible="visible" @update:visible="updateVisible($event)" class="vx-max-w-lg" :padding="false">
        <template v-slot:header>
            <div class="vx-text-2xl vx-pt-6 vx-px-6">
                Add User to Group
            </div>
        </template>

        <template v-slot:subheader>
            <div class="vx-px-6 vx-break-words" data-test="subheader">
                {{ props.user.name }} - {{ props.user.email }}
            </div>
        </template>

        <div class="vx-p-6">
            <div class="vx-flex vx-flex-col vx-gap-4">
                <vx-alert v-if="error" color="danger" :closeable="false">
                    {{ error }}
                </vx-alert>
                <div>
                    <vx-label>Group Name</vx-label>
                    <select-group v-model="groupId" size="lg" :relativeDropdown="true" :error="displayFirstError(validate.groupId)"></select-group>
                </div>
                <div>
                    <vx-label>Group Role</vx-label>
                    <vx-selectable v-model="role"
                        relative-dropdown
                        size="lg"
                        :reduce="option => option.value"
                        :options="groupRolesOptions()"
                        :clearable="false"
                        :error="displayFirstError(validate.role)"
                    >
                    </vx-selectable>
                </div>
            </div>
        </div>

        <template v-slot:footer>
            <div class="vx-flex vx-flex-col-reverse vx-gap-4 lg:vx-flex-row">
                <vx-button @click.prevent="updateVisible(false)"
                    type="button"
                    class="vx-grow lg:vx-w-20 lg:vx-grow-0"
                    size="lg"
                    color="muted">
                    Cancel
                </vx-button>
                <vx-button @click.prevent="submit"
                    data-test="add-button"
                    :loading="submitting"
                    type="button"
                    class="vx-grow"
                    size="lg"
                    color="primary">
                    Add to Group
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>
<script setup>
import { ref } from 'vue';
import { VxAlert, VxButton, VxLabel, VxModal, VxSelectable } from '@voxie/frontend-components';
import { groupUsersClient } from '~/services';
import { groupRolesOptions } from '~/constants/roles';
import { HTTP_INTERNAL_SERVER_ERROR, HTTP_OK, HTTP_CREATED } from '~/support/HttpStatuses';
import { DEFAULT_GROUP_ROLE } from '~/constants/roles';
import { useToasted } from '~/composables/useToasted';
import useVuelidate from '@vuelidate/core';
import { displayFirstError } from '~/utils/validation';
import { required } from '@vuelidate/validators';
import SelectGroup from '../../../general/SelectGroup.vue';

const visible = defineModel('visible', {
    type: Boolean,
    default: false,
});

const props = defineProps({
    user: {
        type: Object,
        required: true,
    }
});

const emit = defineEmits(['addedToGroup', 'updatedGroupUser']);

const toasted = useToasted();
const teamId = Spark.state.currentTeam.id;
const submitting = ref(false);
const error = ref('');

const groupId = ref('');
const role = ref(DEFAULT_GROUP_ROLE);

const validate = useVuelidate({
    groupId: { required },
    role: { required },
}, { groupId: groupId, role: role });

const updateVisible = (isVisible) => {
    error.value = '';
    groupId.value = '';
    role.value = DEFAULT_GROUP_ROLE;
    validate.value.$reset();
    visible.value = isVisible;
}

const submit = async () => {
    if (submitting.value) {
        return;
    }

    const isValid = await validate.value.$validate();

    if (!isValid) {
        return;
    }

    error.value = '';
    submitting.value = true;

    try {
        const response = await groupUsersClient.upsertGroupUser(teamId, groupId.value, props.user.id, { role: role.value });

        if (response.status === HTTP_CREATED) {
            emit('addedToGroup', props.user.id);
        } else if (response.status === HTTP_OK) {
            emit('updatedGroupUser');
        }

        updateVisible(false);
    } catch (e) {
        if (!e?.response?.status || e.response.status >= HTTP_INTERNAL_SERVER_ERROR) {
            error.value = 'Something went wrong.'
        } else {
            error.value = e.response?.data?.message || 'Something went wrong.';
        }
        toasted.error(error.value);
    } finally {
        submitting.value = false;
    }
}
</script>
